// What Help's player is doing with one walkthrough (D22): loading its video,
// showing it, or failed. A link that ran out is not a failure: the page asks
// the server for a new one and the player goes on from the same second with
// no message. A link runs out only once it has been out for a while, so a
// fresh link that fails did not run out: the video failed, and the page says
// so in place. That also bounds the asking: never more than one new link a
// minute, however often the video fails.

export type PlayerState = 'loading' | 'ready' | 'error';

export interface Playback {
	state: PlayerState;
	/** In the error state, whether to offer Retry: only after a network error (.claude/rules/web.md). */
	retry: boolean;
}

/** A link younger than this that fails did not run out. */
export const FRESH_LINK_MS = 60_000;

/** A video that has not loaded this long after it was asked for has stalled. */
export const STALL_MS = 20_000;

/** A walkthrough just opened, or Retry found its link. */
export function opening(): Playback {
	return { state: 'loading', retry: false };
}

/** The video can play. */
export function loaded(): Playback {
	return { state: 'ready', retry: false };
}

/**
 * The video stopped loading or playing. A link out for a minute or more may
 * have run out: ask for a new one, and say nothing. A fresh one did not, so
 * the video failed, and Retry shows only when the network did. A browser
 * reports an HTTP error the same way as a file it cannot play, so no other
 * failure can say Retry would help (owner, 2026-10-10).
 */
export function afterError(
	p: Playback,
	linkAgeMs: number,
	network: boolean,
): { next: Playback; relink: boolean } {
	if (p.state === 'error') return { next: p, relink: false };
	if (linkAgeMs >= FRESH_LINK_MS) return { next: p, relink: true };
	return { next: { state: 'error', retry: network }, relink: false };
}

/** No new link came. The server's answer says whether asking again can help. */
export function noLink(retry: boolean): Playback {
	return { state: 'error', retry };
}
